import { canAccessManagement, canAccessPublic, ChangePasswordData, ForgotPasswordData, LoginCredentials, LoginResponseData, RegisterData, ResetPasswordData } from '@/domain/auth/auth'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi } from '../../rest/modules/auth-api'
import { Paths } from '../../routes/paths'
import { useAuthStore } from '../../store/useAuthStore'
import { useToast } from '../use-toast'
import { ApiResponse } from '@/domain'

export const useLogin = (loginType: 'management' | 'public' = 'public', onSuccessCallback?: (user: any) => void) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { login } = useAuthStore()

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => await authApi.login(credentials),
    onSuccess: (data: LoginResponseData) => {
      if (!data) {
        throw new Error('No data received from login')
      }

      // Update auth store
      login(data.user, data.tokens.accessToken)

      queryClient.clear()

      // Show success message
      toast({
        title: "¡Bienvenido!",
        description: `Sesión iniciada correctamente con el email ${data.user.email}.`,
        variant: "success",
      })

      // Call the success callback if provided, otherwise use default redirect logic
      if (onSuccessCallback) {
        onSuccessCallback(data.user)
      } else {
        // Role-based redirect logic (fallback for backward compatibility)
        if (loginType === 'management') {
          if (canAccessManagement(data.user)) {
            window.location.href = Paths.ManagementDashboard
          } else {
            toast({
              title: "Acceso Denegado",
              description: "No tienes permisos para acceder al panel de administración.",
              variant: "destructive",
            })
            window.location.href = Paths.PublicLogin
          }
        } else {
          if (canAccessPublic(data.user)) {
            window.location.href = Paths.PublicDashboard
          } else {
            toast({
              title: "Acceso Denegado",
              description: "No tienes permisos para acceder al sitio público.",
              variant: "destructive",
            })
            window.location.href = Paths.Home
          }
        }
      }
    },
    onError: (response: ApiResponse<LoginResponseData>) => {
      toast({
        title: "Error de Inicio de Sesión",
        description: response.error || response.message || "Error de red, servidor no disponible",
        variant: "destructive",
      })
    },
  })
}

export function useRegister() {
  const { toast } = useToast()
  const { login } = useAuthStore()

  return useMutation({
    mutationFn: async (data: RegisterData) => await authApi.register(data),
    onSuccess: (data) => {
      if (!data) {
        throw new Error('No data received from register')
      }

      // Update auth store
      login(data.user, data.tokens.accessToken)

      toast({
        title: "Cuenta Creada",
        description: `¡Bienvenido ${data.user.fullName || data.user.email}! Tu cuenta ha sido creada exitosamente.`,
        variant: "success",
      })

      setTimeout(() => {
        // Redirect based on user role
        if (data && canAccessManagement(data.user)) {
          window.location.href = Paths.ManagementDashboard
        } else {
          window.location.href = Paths.PublicDashboard
        }
      }, 2000)
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: "Error de Registro",
        description: response.message || response.error || "Error al crear la cuenta.",
        variant: "destructive",
      })
    },
  })
}

export function useLogout(logoutType: 'management' | 'public' = 'public', onSuccessCallback?: () => void) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { logout } = useAuthStore()

  return useMutation({
    mutationFn: async () => await authApi.logout(),
    onSuccess: () => {
      // Update auth store
      logout()
      queryClient.clear()

      toast({
        title: "Sesión Cerrada",
        description: "Has cerrado sesión exitosamente.",
        variant: "success",
      })

      // Call the success callback if provided, otherwise use default redirect logic
      if (onSuccessCallback) {
        onSuccessCallback()
      } else {
        // Redirect based on logout type (fallback for backward compatibility)
        const redirectPath = logoutType === 'management' ? Paths.ManagementLogin : Paths.PublicLogin
        window.location.href = redirectPath
      }
    },
    onError: (response: ApiResponse<any>) => {

      // Update auth store
      logout()
      queryClient.clear()

      toast({
        title: "Sesión Cerrada",
        description: "Has sido desconectado.",
        variant: "warning",
      })

      // Call the success callback if provided, otherwise use default redirect logic
      if (onSuccessCallback) {
        onSuccessCallback()
      } else {
        // Redirect based on logout type (fallback for backward compatibility)
        const redirectPath = logoutType === 'management' ? Paths.ManagementLogin : Paths.PublicLogin
        window.location.href = redirectPath
      }
    },
  })
}

export function useForgotPassword() {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: ForgotPasswordData) => await authApi.forgotPassword(data),
    onSuccess: () => {
      toast({
        title: "Email Enviado",
        description: "Se ha enviado un enlace de recuperación a tu correo electrónico.",
        variant: "success",
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: "Error",
        description: response.message || response.error || "Error al enviar el email de recuperación.",
        variant: "destructive",
      })
    },
  })
}

export function useResetPassword() {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: ResetPasswordData) => await authApi.resetPassword(data),
    onSuccess: () => {
      toast({
        title: "Contraseña Restablecida",
        description: "Tu contraseña ha sido restablecida exitosamente. Puedes iniciar sesión ahora.",
        variant: "success",
      })

      setTimeout(() => {
        window.location.href = Paths.PublicLogin
      }, 2000)
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: "Error",
        description: response.message || response.error || "Error al restablecer la contraseña.",
        variant: "destructive",
      })
    },
  })
}

export function useChangePassword() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { logout } = useAuthStore()

  return useMutation({
    mutationFn: async (data: ChangePasswordData) => await authApi.changePassword(data),
    onSuccess: () => {
      logout()
      queryClient.clear()

      toast({
        title: "Contraseña Actualizada",
        description: "Tu contraseña ha sido actualizada exitosamente. Por favor, inicia sesión nuevamente.",
        variant: "success",
      })

      setTimeout(() => {
        window.location.href = Paths.ManagementLogin
      }, 2000)
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: "Error",
        description: response.message || response.error || "Error al actualizar la contraseña.",
        variant: "destructive",
      })
    },
  })
}
